import React, {
  cloneElement,
  CSSProperties,
  DOMAttributes,
  ForwardedRef,
  HTMLAttributes,
} from 'react';
import { Transition, TransitionStatus } from 'react-transition-group';
import { useDialog } from 'react-aria/useDialog';
import { FocusScope } from 'react-aria/FocusScope';
import { useModalOverlay } from 'react-aria/useModalOverlay';
import { AriaOverlayProps } from 'react-aria/useOverlay';
import { Overlay } from 'react-aria/Overlay';
import { mergeProps } from 'react-aria/mergeProps';
import { useId } from 'react-aria/useId';
import { useObjectRef } from 'react-aria/useObjectRef';
import { getWrappedElement } from '@react-spectrum/utils';
import { useOverlayTriggerState } from 'react-stately/useOverlayTriggerState';
import { AriaDialogProps } from '@react-types/dialog';
import { FocusableElement } from '@react-types/shared';

import { Backdrop } from '../../Backdrop';
import { useThemeContext } from '../../SapphireProvider';
import { GlobalDomAttributes } from '../../utils/types';

export type ModalProps = AriaOverlayProps &
  AriaDialogProps &
  GlobalDomAttributes & {
    backdropProps?: HTMLAttributes<HTMLDivElement>;
    onCloseTransitionDone?: () => void;
    children: (props: {
      titleProps: DOMAttributes<FocusableElement>;
      transitionState: TransitionStatus;
    }) => React.ReactNode;
  };

/**
 * This is extracted in a variable to share the same zIndex in modal popover underlay and here.
 * The code here can be refactored more to reuse the same Underlay component here and in modal
 * popover. The Underlay component can have an option to include the backdrop or be transparent.
 */
export const MODAL_UNDERLAY_STYLES = (overlayZIndex = 100): CSSProperties => ({
  position: 'fixed',
  zIndex: overlayZIndex,
  inset: 0,
});

/**
 * Internal helper component for building accessible dialog modals without
 * a trigger (for the moment).
 *
 * A *modal* is a piece of UI on top of the whole screen which is auto focused
 * and it contains the focus scope. It also disables page scroll and adds
 * "aria-hidden" to everything except the modal.
 *
 * A *dialog* refers to a a few accessibility properties marking up the
 * overlayed content as a dialog. The "titleProps" from the render function in
 * "children" should be applied on the element which contains the title text of
 * the dialog.
 *
 * In react-aria a modal and a dialog are separate constructs, but to get a
 * fully accessible modal you always need to render a dialog inside; hence this
 * component. https://react-spectrum.adobe.com/react-aria/useModalOverlay.html#features
 *
 * TODO: add support for optionally binding the trigger to the modal (see
 * `useOverlayTrigger` from `@react-aria/overlays`).
 */
export const Modal = React.forwardRef(function Modal(
  { children, onCloseTransitionDone, backdropProps = {}, ...props }: ModalProps,
  ref: ForwardedRef<HTMLDivElement>
) {
  const { isOpen = true, onClose } = props;
  const containerRef = useObjectRef<HTMLDivElement>(ref);
  const state = useOverlayTriggerState({
    isOpen,
    onOpenChange: (isOpen) => {
      if (!isOpen) {
        onClose && onClose();
      }
    },
  });
  /* useDialog does not label the dialog if it is not rendered immediately.
   * Because we wrap the dialog with <Transition> we need to label the dialog
   * manually.
   *
   * For more contexts see the useSlotId here
   * https://github.com/adobe/react-spectrum/blob/main/packages/%40react-aria/utils/src/useId.ts
   */
  const id = useId();
  const titleId = props['aria-label'] ? undefined : id;

  const { modalProps, underlayProps } = useModalOverlay(
    props,
    state,
    containerRef
  );
  const { dialogProps, titleProps } = useDialog(props, containerRef);
  const { overlayZIndex } = useThemeContext();

  return (
    <Transition
      in={isOpen}
      mountOnEnter
      nodeRef={containerRef}
      unmountOnExit
      onExited={onCloseTransitionDone}
      timeout={200}
    >
      {(transitionState) => (
        <Overlay>
          <div style={MODAL_UNDERLAY_STYLES(overlayZIndex)}>
            <Backdrop
              underlayProps={mergeProps(underlayProps, backdropProps)}
              isVisible={['entering', 'entered'].includes(transitionState)}
            >
              <FocusScope contain restoreFocus autoFocus>
                {cloneElement(
                  getWrappedElement(
                    children({
                      titleProps: mergeProps(titleProps, { id: titleId }),
                      transitionState,
                    })
                  ),
                  {
                    ...mergeProps(dialogProps, modalProps, {
                      'aria-labelledby': props['aria-labelledby'] || titleId,
                    }),
                    ref: containerRef,
                  }
                )}
              </FocusScope>
            </Backdrop>
          </div>
        </Overlay>
      )}
    </Transition>
  );
});
